/**
 * Real-time notification context and provider
 *
 * Establishes a WebSocket connection for receiving real-time notifications.
 * Components can subscribe to specific notification types.
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { getWebSocketBaseUrl } from '../api/config';
import type { Notification, NotificationType, NotificationHandler, NotificationContextType } from './types';

/**
 * Exported so tests can wrap a component in an inert provider (see
 * `test-utils`); application code always goes through the provider component and
 * the `useRealtimeNotifications` hook below.
 */
export const RealtimeNotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

/**
 * WebSocket close codes used by the notification system.
 * Standard codes (1000-1015) are defined by RFC 6455.
 * Application codes (4000-4999) are for our use.
 */
export const WebSocketCloseCode = {
  NORMAL: 1000,           // Normal closure
  GOING_AWAY: 1001,       // Server shutdown
  INVALID_TOKEN: 4001,    // Token malformed or invalid signature
  EXPIRED_TOKEN: 4002,    // Token expired - should refresh and reconnect
  MISSING_CLAIMS: 4003,   // Token missing user_id or tenant_id
  CONNECTION_LIMIT: 4004, // User exceeded max connections
  SERVER_ERROR: 4005,     // Internal server error
  SESSION_BLOCKED: 4006,  // Session blocked (e.g. tenant offboarded) - don't reconnect
} as const;

// WebSocket base URL — derived from the API base (not window.location), so it
// works on bundled native apps where the WebView origin is localhost, not the
// API host. See getWebSocketBaseUrl in api/config.ts.
const getWsUrl = () => getWebSocketBaseUrl();

/**
 * Get reconnection delay based on close code.
 * - Expired token: 2s (give auth time to refresh)
 * - Server shutdown: 5s (wait for restart)
 * - Connection limit: 10s (don't spam reconnects)
 * - Other errors: 3s
 */
const getReconnectDelay = (code: number): number => {
  switch (code) {
    case WebSocketCloseCode.EXPIRED_TOKEN:
      return 2000;
    case WebSocketCloseCode.GOING_AWAY:
      return 5000;
    case WebSocketCloseCode.CONNECTION_LIMIT:
      return 10000;
    default:
      return 3000;
  }
};

interface RealtimeNotificationProviderProps {
  children: React.ReactNode;
}

export function RealtimeNotificationProvider({ children }: RealtimeNotificationProviderProps) {
  const { accessToken, isAuthenticated, refreshAuth, user } = useAuth();
  // Extract tenant_id to a primitive to avoid effect re-runs when user object reference changes
  const tenantId = user?.tenant_id;
  const [isConnected, setIsConnected] = useState(false);
  const [lastNotification, setLastNotification] = useState<Notification | null>(null);

  // Handlers map: type -> set of handlers
  const handlersRef = useRef<Map<string, Set<NotificationHandler>>>(new Map());
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Active per-entity thread viewerships: entity_id -> refcount. Sent to the
  // backend as subscribe_thread/unsubscribe_thread control messages so scoped
  // entity.thread.updated cues are delivered only to viewers; re-registered in
  // ws.onopen after any reconnect.
  const threadSubsRef = useRef<Map<string, number>>(new Map());
  // Track current token in ref to avoid effect dependency issues
  const accessTokenRef = useRef<string | null>(null);
  // Track refreshAuth in ref to avoid effect dependency issues
  const refreshAuthRef = useRef(refreshAuth);

  // Keep refs in sync
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    refreshAuthRef.current = refreshAuth;
  }, [refreshAuth]);

  // Track connection generation to handle stale onclose callbacks
  const connectionGenerationRef = useRef(0);

  // Subscribe to a notification type
  const subscribe = useCallback((type: NotificationType | '*', handler: NotificationHandler) => {
    if (!handlersRef.current.has(type)) {
      handlersRef.current.set(type, new Set());
    }
    handlersRef.current.get(type)!.add(handler);

    // Return unsubscribe function
    return () => {
      handlersRef.current.get(type)?.delete(handler);
    };
  }, []);

  // Register/deregister interest in a specific entity's thread. Refcounted so N
  // components viewing the same entity share one backend subscription; the
  // control message only fires on the 0↔1 transition.
  const subscribeThread = useCallback((entityId: string) => {
    const map = threadSubsRef.current;
    const count = map.get(entityId) ?? 0;
    map.set(entityId, count + 1);
    if (count === 0) {
      const ws = wsRef.current;
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'subscribe_thread', entity_id: entityId }));
      }
    }
    return () => {
      const cur = map.get(entityId) ?? 0;
      if (cur <= 1) {
        map.delete(entityId);
        const ws = wsRef.current;
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'unsubscribe_thread', entity_id: entityId }));
        }
      } else {
        map.set(entityId, cur - 1);
      }
    };
  }, []);

  // Dispatch notification to handlers
  const dispatchNotification = useCallback((notification: Notification) => {
    setLastNotification(notification);

    // Call specific type handlers
    const typeHandlers = handlersRef.current.get(notification.type);
    if (typeHandlers) {
      typeHandlers.forEach((handler) => {
        try {
          handler(notification);
        } catch (err) {
          console.error('Notification handler error:', err);
        }
      });
    }

    // Call wildcard handlers
    const wildcardHandlers = handlersRef.current.get('*');
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => {
        try {
          handler(notification);
        } catch (err) {
          console.error('Notification handler error:', err);
        }
      });
    }
  }, []);

  // Stabilize token dependency - only re-run effect when token presence changes, not on every refresh
  const hasToken = !!accessToken;

  // Connect/disconnect based on auth state - single effect with minimal dependencies
  useEffect(() => {
    // Don't connect if not authenticated or user has no tenant (pending_setup)
    if (!isAuthenticated || !hasToken || !tenantId) {
      // Clean up any existing connection
      connectionGenerationRef.current++;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close(WebSocketCloseCode.NORMAL, 'User logout');
        wsRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    // Already connected with same token - do nothing
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Already connecting - do nothing
    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Increment generation to invalidate any pending reconnects from old connections
    connectionGenerationRef.current++;
    const thisGeneration = connectionGenerationRef.current;

    const connectWebSocket = () => {
      // Check if this connection attempt is still valid
      if (thisGeneration !== connectionGenerationRef.current || !accessTokenRef.current) {
        return;
      }

      // Build URL with current token (not the stale one from effect closure)
      const currentWsUrl = `${getWsUrl()}/api/v1/ws/notifications?token=${encodeURIComponent(accessTokenRef.current)}`;

      try {
        const ws = new WebSocket(currentWsUrl);

        ws.onopen = () => {
          // Check if still valid generation
          if (thisGeneration !== connectionGenerationRef.current) {
            ws.close(WebSocketCloseCode.NORMAL, 'Stale connection');
            return;
          }
          // Connection status logged only at debug level
          setIsConnected(true);

          // Re-register any active thread viewerships. On a fresh connect the
          // map is empty; after a reconnect this restores the server-side
          // subscriptions that were lost with the previous socket.
          threadSubsRef.current.forEach((_count, entityId) => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'subscribe_thread', entity_id: entityId }));
            }
          });

          // Start ping interval to keep connection alive
          pingIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send('ping');
            }
          }, 30000); // Ping every 30 seconds
        };

        ws.onclose = (event) => {
          // Only log unexpected closes

          // Clear ping interval
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }

          // Ignore onclose from stale connections (different generation)
          if (thisGeneration !== connectionGenerationRef.current) {
            return;
          }

          setIsConnected(false);
          wsRef.current = null;

          // Reconnect logic based on close code
          if (event.code === WebSocketCloseCode.NORMAL) {
            // Normal close - don't reconnect
            return;
          }

          if (event.code === WebSocketCloseCode.INVALID_TOKEN ||
              event.code === WebSocketCloseCode.MISSING_CLAIMS ||
              event.code === WebSocketCloseCode.SESSION_BLOCKED) {
            // Auth is terminally rejected (invalid token, or a blocked session —
            // e.g. the tenant was offboarded). Don't reconnect: retrying would just
            // hammer the endpoint until the token expires. The user must re-auth.
            console.warn('WebSocket auth failed - not reconnecting');
            return;
          }

          // For expired token, try to refresh auth first then reconnect
          if (event.code === WebSocketCloseCode.EXPIRED_TOKEN) {
            refreshAuthRef.current()
              .then(() => {
                // Small delay to ensure accessTokenRef is updated
                reconnectTimeoutRef.current = setTimeout(connectWebSocket, 500);
              })
              .catch((err) => {
                console.warn('Failed to refresh auth for WebSocket:', err);
                // Don't reconnect if refresh fails - user needs to re-login
              });
            return;
          }

          // For all other codes, schedule reconnect with appropriate delay
          const delay = getReconnectDelay(event.code);
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
        };

        ws.onerror = (error) => {
          console.error('Notification WebSocket error:', error);
        };

        ws.onmessage = (event) => {
          // Ignore pong responses
          if (event.data === 'pong') {
            return;
          }

          try {
            const notification = JSON.parse(event.data) as Notification;
            dispatchNotification(notification);
          } catch (err) {
            console.error('Failed to parse notification:', err);
          }
        };

        wsRef.current = ws;
      } catch (err) {
        console.error('Failed to create WebSocket:', err);
      }
    };

    connectWebSocket();

    // Cleanup on unmount or when dependencies change
    return () => {
      // Increment generation so any callbacks from this connection are ignored
      // eslint-disable-next-line react-hooks/exhaustive-deps
      connectionGenerationRef.current++;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      if (wsRef.current) {
        wsRef.current.close(WebSocketCloseCode.NORMAL, 'Cleanup');
        wsRef.current = null;
      }

      setIsConnected(false);
    };
  }, [isAuthenticated, hasToken, tenantId, dispatchNotification]);

  const value: NotificationContextType = {
    isConnected,
    lastNotification,
    subscribe,
    subscribeThread,
  };

  return (
    <RealtimeNotificationContext.Provider value={value}>
      {children}
    </RealtimeNotificationContext.Provider>
  );
}

export function useRealtimeNotifications(): NotificationContextType {
  const context = useContext(RealtimeNotificationContext);
  if (context === undefined) {
    throw new Error('useRealtimeNotifications must be used within a RealtimeNotificationProvider');
  }
  return context;
}
